import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

import { ExpandIcon, FolderIcon, ResendInvitationIcon, SuccessIcon } from '../../../components/Icon';
import ApprovalStatusBadge from '../components/ApprovalStatusBadge';
import type { ApprovalListItem, ApprovalStatusBadgeVariant } from '../types';

interface UseApprovalsColumnsOptions {
  /** Current signed-in user, to render "(You)" next to their own claims. */
  currentUserId?: string;
  /** Opens a record. The full row is passed so the queue can prompt the claim dialog
   * for an Available FNMS pool entry (ACP-247) before navigating. */
  onViewApplication: (row: ApprovalListItem) => void;
  /** Only Foreign National Approvers see the Claim Status column. */
  isForeignNationalApprover: boolean;
  /** ACP-2752: mirrors the retry endpoint's authorization — FN approver or Administrator. */
  canRetryFnms?: boolean;
  /** True while a retry is in flight, so the control cannot be fired twice. */
  isRetryPending?: boolean;
  /** Replays a dead-lettered FNMS submission straight from the queue. */
  onRetryFnms?: (row: ApprovalListItem) => void;
}

function ClaimStatusCell({ row, currentUserId }: { row: ApprovalListItem; currentUserId?: string }) {
  // Unclaimed pool entry — available for any eligible reviewer to claim.
  if (!row.assignedToUserId) {
    return (
      <div className='manage-approvals__claim-status manage-approvals__claim-status--available'>
        <SuccessIcon
          ariaLabel=''
          size={20}
        />
        <span>Available</span>
      </div>
    );
  }

  const claimantName = [row.assignedToFirstName, row.assignedToLastName].filter(Boolean).join(' ').trim();
  const isYou = !!currentUserId && row.assignedToUserId === currentUserId;
  const label = claimantName || 'Claimed';

  return <div className='manage-approvals__claim-status'>{isYou ? `${label} (You)` : label}</div>;
}

function getApprovalStatusBadge(row: ApprovalListItem): ApprovalStatusBadgeVariant | null {
  // ACP-2752: a spent FNMS submission is the one row in this queue that needs attention
  // rather than patience, so it reads as a badge like the rest instead of falling through
  // to the generic title-cased status text.
  if (row.applicationStatus === 'FNMS_SUBMISSION_FAILED') {
    return 'fnms-save-failed';
  }

  if (row.applicationStatus === 'COR_REVIEW' && row.status === 'PENDING') {
    return 'pending-federal-approver-review';
  }

  if (row.applicationStatus !== 'FNMS_REVIEW') {
    return null;
  }

  return row.reviewStage === 'FNMS' && row.status === 'PENDING' && row.pooled === false
    ? 'pending-dci-action'
    : 'pending-dci-review';
}

function formatApplicationStatus(status?: string): string {
  if (!status) {
    return '—';
  }

  return status
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function ApplicationStatusCell({
  row,
  canRetryFnms,
  isRetryPending,
  onRetryFnms,
}: {
  row: ApprovalListItem;
  canRetryFnms: boolean;
  isRetryPending: boolean;
  onRetryFnms?: (row: ApprovalListItem) => void;
}) {
  const badge = getApprovalStatusBadge(row);
  // ACP-2752: a parked submission is the one queue state carrying an action the reviewer can
  // take without opening the record, so the retry lives inside the badge. Gated on the same
  // roles the retry endpoint authorizes, so nobody is offered a control the server refuses.
  const showRetry = badge === 'fnms-save-failed' && canRetryFnms;
  const applicantName = `${row.legalFirstName ?? ''} ${row.legalLastName ?? ''}`.trim();

  if (!badge) {
    return <div className='manage-approvals__combined-status'>{formatApplicationStatus(row.applicationStatus)}</div>;
  }

  return (
    <div className='manage-approvals__combined-status'>
      <ApprovalStatusBadge variant={badge}>
        {showRetry && (
          <button
            aria-label={`Retry sending ${applicantName} to FNMS`}
            className='approval-status-badge__retry'
            disabled={isRetryPending}
            title='Retry Send to FNMS'
            type='button'
            onClick={() => onRetryFnms?.(row)}>
            <ResendInvitationIcon size={20} />
          </button>
        )}
      </ApprovalStatusBadge>
    </div>
  );
}

export function useApprovalsColumns({
  canRetryFnms = false,
  currentUserId,
  isForeignNationalApprover,
  isRetryPending = false,
  onRetryFnms,
  onViewApplication,
}: UseApprovalsColumnsOptions) {
  return useMemo<ColumnDef<ApprovalListItem>[]>(() => {
    const widths = isForeignNationalApprover
      ? {
          action: 104,
          applicationStatus: 182,
          claimStatus: 152,
          contract: 200,
          legalFirstName: 152,
          legalLastName: 152,
          userId: 88,
        }
      : {
          action: 102,
          applicationStatus: 264,
          contract: 200,
          legalFirstName: 188,
          legalLastName: 188,
          userId: 88,
        };

    return [
      {
        accessorKey: 'userId',
        cell: ({ getValue }) => getValue<string | null>() ?? '—',
        enableSorting: true,
        header: 'User ID',
        size: widths.userId,
      },
      {
        accessorKey: 'legalLastName',
        enableSorting: true,
        header: 'Legal Last Name',
        size: widths.legalLastName,
      },
      {
        accessorKey: 'legalFirstName',
        enableSorting: true,
        header: 'Legal First Name',
        size: widths.legalFirstName,
      },
      {
        accessorKey: 'contractNumber',
        enableSorting: true,
        header: 'Contract',
        size: widths.contract,
      },
      {
        accessorFn: (row) => row.applicationStatus ?? '',
        cell: ({ row }) => (
          <ApplicationStatusCell
            canRetryFnms={canRetryFnms}
            isRetryPending={isRetryPending}
            row={row.original}
            onRetryFnms={onRetryFnms}
          />
        ),
        enableSorting: true,
        header: 'Application Status',
        id: 'applicationStatus',
        size: widths.applicationStatus,
      },
      ...(isForeignNationalApprover
        ? ([
            {
              cell: ({ row }) => (
                <ClaimStatusCell
                  currentUserId={currentUserId}
                  row={row.original}
                />
              ),
              enableSorting: false,
              header: 'Claim Status',
              id: 'claimStatus',
              size: widths.claimStatus,
            },
          ] satisfies ColumnDef<ApprovalListItem>[])
        : []),
      {
        cell: ({ row }) => (
          <div className='manage-approvals__action-cell'>
            <button
              aria-expanded={row.getIsExpanded()}
              aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
              className='manage-approvals__action-button action-icon--blue-square'
              title={row.getIsExpanded() ? 'Collapse' : 'Expand'}
              type='button'
              onClick={() => row.toggleExpanded()}>
              <ExpandIcon
                expanded={row.getIsExpanded()}
                size={20}
              />
            </button>
            <button
              aria-label={`View application for ${row.original.legalFirstName} ${row.original.legalLastName}`}
              className='manage-approvals__action-button action-icon--blue-square'
              title='View Application'
              type='button'
              onClick={() => row.original.applicationId && onViewApplication(row.original)}>
              <FolderIcon size={20} />
            </button>
          </div>
        ),
        enableSorting: false,
        header: 'Actions',
        id: 'action',
        size: widths.action,
      },
    ];
  }, [canRetryFnms, currentUserId, isForeignNationalApprover, isRetryPending, onRetryFnms, onViewApplication]);
}
